import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import {
  GetPrivateRegionLeasedTimeMetricsRes,
  GetPrivateRegionNodeHardwareBriefRes,
  GetPrivateRegionStatsOverviewRes,
} from '@/api/type-response'
import { ServerPrivateRegionInfo } from '@/api/type-server'
import { EmptyView, Loading, Trans } from '@/components'
import { ButtonRadio } from '@/components/base/ButtonRadio'
import { formatDatePreset } from '@/utils/formatter'

import { SpecCategory } from '../constants/specCategory'
import AcceleratorCard from './AcceleratorCard'
import { NodeStatusCard } from './NodeStatusCard'
import UsageTimeCard from './UsageTimeCard'
import { UsageTimeChart } from './UsageTimeChart'

type UsageTimeRangeType = 'this_month' | 'last_3_months' | 'last_6_months' | 'last_12_months'

interface DashboardContentProps {
  regionInfo: ServerPrivateRegionInfo | null
  overviewData: GetPrivateRegionStatsOverviewRes | null
  gpuBriefData: GetPrivateRegionNodeHardwareBriefRes | null
  usageMetrics: GetPrivateRegionLeasedTimeMetricsRes | null
  chartUsageMetrics: GetPrivateRegionLeasedTimeMetricsRes | null
  chartLoading?: boolean
  usageTimeRange: UsageTimeRangeType
  onUsageTimeRangeChange: (value: UsageTimeRangeType) => void
  getUsageTimeRangeText: () => string
}

export function DashboardContent({
  regionInfo,
  overviewData,
  gpuBriefData,
  usageMetrics,
  chartUsageMetrics,
  chartLoading,
  usageTimeRange,
  onUsageTimeRangeChange,
  getUsageTimeRangeText,
}: DashboardContentProps) {
  const location = useLocation()
  const { t } = useTranslation('privateRegion')

  // Allow metrics to be empty (API may fail), but require other data
  if (!regionInfo || !overviewData || !gpuBriefData) {
    return (
      <EmptyView
        title={
          <Trans ns="privateRegion" i18nKey="common.noData">
            No data
          </Trans>
        }
      />
    )
  }

  // Metrics can be null or have empty arrays - handle gracefully
  const safeUsageMetrics = usageMetrics || { metrics: [] }
  const safeChartUsageMetrics = chartUsageMetrics || { metrics: [] }

  // Use region info with fallback
  const region = regionInfo || {
    region: 0,
    resID: '-',
    name: '-',
    owner: '-',
    createTime: { seconds: 0 },
    desc: '-',
  }

  // Prepare data for components
  const {
    nodesAll,
    nodesOnline,
    leasedTimeHour,
    leasedTimeHourRateDelta,
    occupancyRate,
    occupancyRateDelta,
  } = overviewData

  const standbyNodes = nodesAll - nodesOnline

  // Node status data
  const nodeStatusData = [
    { status: 'online' as const, count: nodesOnline, label: t('common.online') },
    { status: 'standby' as const, count: standbyNodes, label: t('common.standby') },
  ]

  // Usage card data (fixed 30 days, for cards)
  const actualUsageData = safeUsageMetrics.metrics.map((m) => ({
    date: formatDatePreset(m.timestamp * 1000, 'day'),
    value: m.leasedHour,
  }))

  // Occupancy rate data (fixed 30 days, for occupancy rate card)
  const occupancyRateData = safeUsageMetrics.metrics.map((m) => ({
    date: formatDatePreset(m.timestamp * 1000, 'day'),
    value: m.totalHour > 0 ? m.leasedHour / m.totalHour : 0,
  }))

  // Chart data (dynamic time range, for the big chart)
  const chartActualUsageData = safeChartUsageMetrics.metrics.map((m) => ({
    date: formatDatePreset(m.timestamp * 1000, 'day'),
    value: m.leasedHour,
  }))

  const chartTotalAvailableData = safeChartUsageMetrics.metrics.map((m) => ({
    date: formatDatePreset(m.timestamp * 1000, 'day'),
    count: m.totalHour,
  }))

  // Transform accelerator data from API to component format
  const acceleratorPieChartData =
    gpuBriefData.brief.modelStats.length > 0
      ? gpuBriefData.brief.modelStats.map((stat) => ({
          type: stat.model,
          nodeCount: stat.online,
          gpuCount: stat.vGPUs,
          category: stat.category,
        }))
      : [{ type: 'No Data', nodeCount: 0, gpuCount: 0, category: SpecCategory.GPU }]

  // Filter GPU category stats for accelerator cards
  const gpuModelStats = gpuBriefData.brief.modelStats.filter(
    (stat) => stat.category === SpecCategory.GPU,
  )
  const gpuOnline = gpuModelStats.reduce((sum, stat) => sum + stat.online, 0)
  const gpuLeased = gpuModelStats.reduce((sum, stat) => sum + stat.leased, 0)

  const acceleratorStats = {
    available: gpuOnline,
    standby: gpuOnline - gpuLeased,
    inUse: gpuLeased,
    usageRate: gpuOnline > 0 ? (gpuLeased / gpuOnline) * 100 : 0,
  }

  return (
    <div>
      <div className="mb-8">
        <div className="h6 mb-4">
          <Trans ns="privateRegion" i18nKey="regionInfo.title">
            Private Region Information
          </Trans>
        </div>
        <div className="border border-neutral-200 rounded-2xl p-4 grid grid-cols-3 gap-4">
          <div className="flex flex-col justify-between gap-1">
            <div className="t-xs text-secondary">
              <Trans ns="privateRegion" i18nKey="regionInfo.regionId">
                Region ID
              </Trans>
            </div>
            <div className="t-sb">{region.resID}</div>
          </div>
          <div className="flex flex-col justify-between gap-1">
            <div className="t-xs text-secondary">
              <Trans ns="privateRegion" i18nKey="regionInfo.regionName">
                Region Name
              </Trans>
            </div>
            <div className="t-sb">{region.name}</div>
          </div>
          <div className="flex flex-col justify-between gap-1">
            <div className="t-xs text-secondary">
              <Trans ns="privateRegion" i18nKey="regionInfo.owner">
                Owner
              </Trans>
            </div>
            <div className="t-sb">{region.owner}</div>
          </div>
          <div className="flex flex-col justify-between gap-1">
            <div className="t-xs text-secondary">
              <Trans ns="privateRegion" i18nKey="regionInfo.createTime">
                Create Time
              </Trans>
            </div>
            <div className="t-sb">
              {region.createTime.seconds === 0
                ? '-'
                : formatDatePreset(region.createTime.seconds * 1000, 'second')}
            </div>
          </div>
          <div className="flex flex-col justify-between gap-1">
            <div className="t-xs text-secondary">
              <Trans ns="privateRegion" i18nKey="regionInfo.description">
                Description
              </Trans>
            </div>
            <div className="t-sb">{region.desc}</div>
          </div>
        </div>
      </div>
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="h6">
            <Trans ns="privateRegion" i18nKey="computeNode.title">
              Compute Node
            </Trans>
          </div>
          <Link to={`/private-region/compute${location.search}`} className="t-sb text-highlight">
            <Trans ns="privateRegion" i18nKey="common.viewMore">
              View More →
            </Trans>
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <UsageTimeCard
            title={t('usageTime.usageTimeThisMonth')}
            value={leasedTimeHour}
            changePercent={leasedTimeHourRateDelta * 100}
            isPositiveChange={leasedTimeHourRateDelta > 0}
            chartData={actualUsageData}
          />
          <UsageTimeCard
            title={t('usageTime.occupancyRateThisMonth')}
            value={occupancyRate}
            isValuePercent={true}
            changePercent={occupancyRateDelta * 100}
            isPositiveChange={occupancyRateDelta > 0}
            chartData={occupancyRateData}
          />

          <NodeStatusCard nodes={nodeStatusData} total={nodesAll} />
        </div>
      </div>

      {/* Second Row: Usage Time Chart */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="h6">
              <Trans ns="privateRegion" i18nKey="usageTime.title">
                Usage Time
              </Trans>
            </div>
            <div className="t-sm text-secondary">
              <Trans ns="privateRegion" i18nKey="usageTime.usageTimesIn">
                Usage Times in
              </Trans>{' '}
              {getUsageTimeRangeText()}
            </div>
          </div>

          <ButtonRadio
            items={[
              { label: t('timeRange.thisMonth'), value: 'this_month' },
              { label: t('timeRange.last3Months'), value: 'last_3_months' },
              { label: t('timeRange.last6Months'), value: 'last_6_months' },
              { label: t('timeRange.last12Months'), value: 'last_12_months' },
            ]}
            value={usageTimeRange}
            onChange={(value) => onUsageTimeRangeChange(value as UsageTimeRangeType)}
          />
        </div>
        {chartLoading ? (
          <div
            className="w-full bg-panel-white p-6 rounded-2xl border border-neutral-200 flex items-center justify-center"
            style={{ height: '450px' }}
          >
            <Loading />
          </div>
        ) : (
          <UsageTimeChart
            actualUsageData={chartActualUsageData}
            totalAvailableData={chartTotalAvailableData}
          />
        )}
      </div>

      {/* Accelerator & GPUs */}
      <div>
        <div className="h6 mb-4">
          <Trans ns="privateRegion" i18nKey="accelerator.title">
            Accelerator & GPUs
          </Trans>
        </div>

        <AcceleratorCard stats={acceleratorStats} pieChartData={acceleratorPieChartData} />
      </div>
    </div>
  )
}
